"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useDropContent } from "./useDropContent";
import DropLoading from "./DropLoading";
import DropNotFound from "./DropNotFound";
import ContentDisplay from "./ContentDisplay";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import { parseStandaloneMediaUrl } from "./media-utils";
interface WaveDropReplyProps {
  readonly dropId: string;
  readonly dropPartId: number;
  readonly maybeDrop: ApiDrop | null;
  readonly onReplyClick: (serialNo: number) => void;
}

/**
 * Component to display a reply in a wave drop
 */
export default function WaveDropReply(t0) {
  const $ = _c(13);
  const {
    dropId,
    dropPartId,
    maybeDrop,
    onReplyClick
  } = t0;
  const {
    drop,
    content,
    isLoading
  } = useDropContent(dropId, dropPartId, maybeDrop);
  let t1;
  bb0: {
    if (content.apiMedia.length > 0 || content.segments.length !== 1) {
      t1 = content;
      break bb0;
    }
    const [segment] = content.segments;
    if (segment?.type !== "text") {
      t1 = content;
      break bb0;
    }
    let t2;
    if ($[0] !== segment.content) {
      t2 = parseStandaloneMediaUrl(segment.content);
      $[0] = segment.content;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const standaloneMedia = t2;
    if (!standaloneMedia) {
      t1 = content;
      break bb0;
    }
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = [];
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== standaloneMedia) {
      t4 = {
        segments: t3,
        apiMedia: [standaloneMedia]
      };
      $[3] = standaloneMedia;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t1 = t4;
  }
  const replyPreviewContent = t1;
  let t2;
  if ($[5] !== drop || $[6] !== isLoading || $[7] !== onReplyClick || $[8] !== replyPreviewContent) {
    t2 = () => {
      if (isLoading) {
        return <DropLoading />;
      }
      if (!drop?.author.handle) {
        return <DropNotFound />;
      }
      return <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-x-1.5"><div className="tw-relative tw-z-10 tw-h-6 tw-w-6 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-md tw-bg-iron-800">{drop.author.pfp ? <Image src={resolveIpfsUrlSync(drop.author.pfp)} alt={`${drop.author.handle}'s avatar`} fill={true} sizes="24px" className="tw-rounded-md tw-bg-transparent tw-object-contain" /> : <div className="tw-h-full tw-w-full tw-rounded-md tw-bg-iron-900 tw-ring-1 tw-ring-inset tw-ring-white/10" />}</div><div className="tw-min-w-0 tw-flex-1"><p className="tw-mb-0 tw-flex tw-min-w-0 tw-items-center xl:tw-pr-24"><Link href={`/${drop.author.handle}`} className="tw-mr-1 tw-flex-shrink-0 tw-text-sm tw-font-medium tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{drop.author.handle}</Link><ContentDisplay content={replyPreviewContent} onClick={() => onReplyClick(drop.serial_no)} className="tw-min-w-0 tw-flex-1 tw-overflow-hidden" textClassName="tw-min-w-0 tw-overflow-hidden" linkify={false} /></p></div></div>;
    };
    $[5] = drop;
    $[6] = isLoading;
    $[7] = onReplyClick;
    $[8] = replyPreviewContent;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const renderDropContent = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-absolute tw-left-5 tw-top-2.5 tw-w-6 tw-cursor-pointer tw-rounded-tl-[12px] tw-border-0 tw-border-l-[1.5px] tw-border-t-[1.5px] tw-border-solid tw-border-iron-700" style={{
      height: "calc(100% - 3px)"
    }} />;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== renderDropContent) {
    t4 = <div className="tw-relative tw-mb-3" data-text-selection-exclude="true">{t3}<div className="tw-ml-[52px] tw-flex tw-items-center tw-gap-x-1.5"><div data-testid="wave-drop-reply-fixed-container" className="tw-h-[24px] tw-min-h-[24px] tw-max-h-[24px] tw-flex tw-w-full tw-items-center tw-overflow-hidden">{renderDropContent()}</div></div></div>;
    $[11] = renderDropContent;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}